import {Col, Container, Row} from "react-grid-system";
import AccordionWithHeader from "../../components/Accordion/AccordionWithHeader";
import {StrategyListHeader} from "./StrategyListHeader";
import React, {useMemo} from "react";
import {StrategyDetailsSectionInfo, StrategyType} from "../WealthPOTypes";
import {AccordionItemWithActionMenu, DropdownItem, Icon} from "../../components";
import {COLOR_ENABLED_WPO_STRATEGY, COLOR_LIABILITIES} from "../../constants/colors";
import {StrategyAccordionItemHeader} from "./StrategyAccordionItemHeader";
import {convertStrategyTypeToString, sortStrategyDetails} from "../WealthPOUtils";
import {TableActionDropdownMenu} from "../../components/Table/TableActionDropdownMenu";
import {wealthPOApiClient} from "../WealthPOApiClient";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {useHistory, useParams} from "react-router-dom";
import {RouteWithId} from "../../routes/types";
import classNames from "classnames";

interface StrategyContentProps {
    refreshStrategiesSummary: () => void;
    strategyDetailsList: StrategyDetailsSectionInfo[];
    isContinued: boolean;
    isInReportPreview: boolean;
}

const StrategyContent: React.FC<StrategyContentProps> = ({
                                                             refreshStrategiesSummary,
                                                             strategyDetailsList,
                                                             isContinued,
                                                             isInReportPreview,
                                                         }: StrategyContentProps) => {
    const {id} = useParams<RouteWithId>();
    const history = useHistory();
    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const hasStrategies = strategyDetailsList.length !== 0;

    const enableOrDisableStrategy = (strategyDetails: StrategyDetailsSectionInfo) => {
        const requestBody = {
            ...strategyDetails,
            enabled: !strategyDetails.enabled
        }

        wealthPOApiClient.editStrategyEnabledStatus(id, requestBody)
            .then(() => refreshStrategiesSummary());
    }

    const editStrategy = (strategyId: string, strategyType: StrategyType) => {
        if (strategyType === StrategyType.STATE_OF_RESIDENCY) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditStateOfResidency/${strategyId}`);
        } else if (strategyType === StrategyType.ANNUAL_GIFTING) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditAnnualGifting/${strategyId}`);
        } else if (strategyType === StrategyType.GIFT_TO_GRANTOR_TRUST) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditGiftToGrantorTrust/${strategyId}`);
        } else if (strategyType === StrategyType.TWO_YEAR_GRAT) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditTwoYearGRAT/${strategyId}`);
        } else if (strategyType === StrategyType.FIVE_YEAR_GRAT) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditFiveYearGRAT/${strategyId}`);
        } else if (strategyType === StrategyType.ROLLING_GRAT) {
            history.push(`/Profile/${id}/ClientProfile/WealthPlanOptimizer/EditRollingGRAT/${strategyId}`);
        }
    }

    const deleteStrategy = (type: StrategyType, strategyId: string) => {
        if (type === StrategyType.STATE_OF_RESIDENCY) {
            wealthPOApiClient.deleteStateOfResidencyStrategy(id, strategyId)
                .then(() => refreshStrategiesSummary())
        } else if ([
            StrategyType.ANNUAL_GIFTING,
            StrategyType.GIFT_TO_GRANTOR_TRUST,
            StrategyType.TWO_YEAR_GRAT,
            StrategyType.FIVE_YEAR_GRAT,
            StrategyType.ROLLING_GRAT
        ].includes(type)) {
            wealthPOApiClient.deleteStrategy(id, strategyId)
                .then(() => refreshStrategiesSummary())
        }
    }

    const memoizedStrategyDetailsList = useMemo(() => {
        let sortedStrategies: StrategyDetailsSectionInfo[] = sortStrategyDetails(strategyDetailsList);
        return sortedStrategies.map(strategyDetails =>
            <AccordionItemWithActionMenu
                key={strategyDetails.id}
                uuid={strategyDetails.id!}
                accentColor={strategyDetails.enabled ? COLOR_ENABLED_WPO_STRATEGY : COLOR_LIABILITIES}
                HeaderComponent={() => StrategyAccordionItemHeader(
                    {
                        description: strategyDetails.description,
                        type: convertStrategyTypeToString(strategyDetails.type),
                        amountToBeneficiaries: strategyDetails.type === StrategyType.STATE_OF_RESIDENCY
                            ? "––"
                            : (strategyDetails.amountToBeneficiaries ?? 0),
                        impactToEstEstateTax: strategyDetails.impactToEstEstateTax ?? 0,
                        enabled: strategyDetails.enabled,
                        isInReportPreview: isInReportPreview
                    }
                )}
                expandable={false}
                actionMenu={
                    <div data-testid="strategy-action-menu">
                        <TableActionDropdownMenu
                            ariaLabel={'Strategy Action Menu'}
                        >

                            <DropdownItem
                                    itemText={`${strategyDetails.enabled ? "Disable" : "Enable"} Strategy`}
                                    value={`${strategyDetails.enabled ? "Disable" : "Enable"} Strategy`}
                                    onClick={() => enableOrDisableStrategy(strategyDetails)}
                            />
                            <DropdownItem
                                itemText="Edit Strategy"
                                value="Edit Strategy"
                                onClick={() => editStrategy(strategyDetails.id!, strategyDetails.type)}
                            />
                            <DropdownItem
                                itemText="Delete Strategy"
                                value="Delete Strategy"
                                disabled={isProfileWithProposalsOrArchived}
                                onClick={() => deleteStrategy(strategyDetails.type,
                                    strategyDetails.id!)}
                            >
                                <Icon name="delete" className="delete-trash-icon"
                                      data-testid="delete-trash-icon"/>
                            </DropdownItem>
                        </TableActionDropdownMenu>
                    </div>
                }
            />)
    }, [strategyDetailsList]);

    return (
        <Container className={classNames({"reporting-summary-details-card": isInReportPreview}, 'summary-details-card')}
                   data-testid='summary-details-card'>
            <Row>
                <Col>
                    <h4>
                        {isContinued
                            ? "Wealth Transfer Strategies (continued)"
                            : "Wealth Transfer Strategies"}
                    </h4>
                </Col>
            </Row>
            {
                (hasStrategies)
                && <Row data-testid='strategy-list' style={{margin: "0px"}}>
                    <Col style={{padding: 0}}>
                        <AccordionWithHeader
                            accordionId={'strategy-list-accordion'}
                            allItems={[]}
                            columnTitles={['Name', 'Strategy', 'Future Value of Gift', 'Impact to  Est. Estate Tax']}
                            renderHeader={true}
                            HeaderComponent={() => StrategyListHeader()}
                        >
                            {
                                memoizedStrategyDetailsList
                            }
                        </AccordionWithHeader>
                    </Col>
                </Row>
            }
        </Container>
    )
}

export default StrategyContent;